import { useEffect, useState } from 'react';
import { accesClientPublicApi } from '../../api/resources';
import { LONGUEUR_MOT_DE_PASSE } from '../../lib/invitation';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { C } from '../../theme/palette';
import { EtapeCode } from '../auth/EtapeCode.jsx';
import { NOM_LOGICIEL } from '../../lib/marque';

const cadre = {
  minHeight: '100vh',
  background: '#F3F4F6',
  padding: '24px 16px 40px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  color: C.ink,
};
const carte = {
  background: '#fff',
  borderRadius: 16,
  padding: 18,
  maxWidth: 460,
  margin: '0 auto 14px',
  boxShadow: '0 1px 3px rgba(0,0,0,.06)',
};
const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 11,
  padding: '12px 13px',
  fontSize: 15,
  fontFamily: 'inherit',
  marginTop: 5,
};
const etiquette = { display: 'block', fontSize: 12, fontWeight: 800, color: C.grey, marginTop: 12 };

/**
 * La page qu'une personne d'une entreprise cliente ouvre par l'invitation du
 * magasin (ou de son responsable) : elle crée son compte — nom, prénom,
 * adresse et mot de passe — puis, au code à six chiffres envoyé à cette
 * adresse, entre dans son espace client.
 *
 * <p>Le client est vouvoyé, comme sur toute page remise par le magasin.
 *
 * @param {object} props propriétés
 * @param {string} props.jeton jeton porté par le lien
 * @returns {import('react').ReactElement} la page
 */
export function PageAccesClient({ jeton }) {
  const [page, setPage] = useState(null);
  const [refus, setRefus] = useState('');
  const [f, setF] = useState({ prenom: '', nom: '', email: '', motDePasse: '', confirmation: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  // Compte créé, session pas encore ouverte : le code est attendu.
  const [code, setCode] = useState(null);

  useEffect(() => {
    let annule = false;
    accesClientPublicApi
      .page(jeton)
      .then((p) => {
        if (annule) return;
        setPage(p);
        setF((x) => ({ ...x, prenom: p.prenom || '', nom: p.nom || '', email: p.email || '' }));
      })
      .catch((e) => {
        if (!annule)
          setRefus(
            e?.status === 404
              ? 'Ce lien d’invitation n’existe pas. Demandez-en un nouveau au magasin.'
              : e?.message ||
                  'Cette invitation n’est plus valable. Demandez-en une nouvelle au magasin.',
          );
      });
    return () => {
      annule = true;
    };
  }, [jeton]);

  const maj = (cle) => (e) => setF((x) => ({ ...x, [cle]: e.target.value }));

  const entre = () => {
    setOuvert(true);
    try {
      window.history.replaceState(null, '', '/');
    } catch {
      // Adresse inchangée : le bouton mène quand même à l'espace.
    }
  };

  const valide = async (e) => {
    e.preventDefault();
    if (!f.prenom.trim() || !f.nom.trim()) return setErreur('Indiquez votre prénom et votre nom.');
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim()))
      return setErreur('Indiquez une adresse e-mail valable.');
    if (f.motDePasse.length < LONGUEUR_MOT_DE_PASSE)
      return setErreur(`Le mot de passe compte au moins ${LONGUEUR_MOT_DE_PASSE} caractères.`);
    if (f.motDePasse !== f.confirmation)
      return setErreur('Les deux mots de passe ne sont pas identiques.');
    setErreur('');
    setEnvoi(true);
    try {
      const reponse = await accesClientPublicApi.accepte(jeton, {
        prenom: f.prenom.trim(),
        nom: f.nom.trim(),
        email: f.email.trim(),
        motDePasse: f.motDePasse,
      });
      if (reponse?.codeRequis) setCode(reponse);
      else entre();
    } catch (err) {
      setErreur(err?.message || 'Le compte n’a pas pu être créé. Réessayez dans un instant.');
    } finally {
      setEnvoi(false);
    }
  };

  if (refus) {
    return (
      <div style={cadre}>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 34 }}>🔗</div>
          <h1 style={{ fontSize: 18 }}>Lien plus valable</h1>
          <p style={{ color: C.grey, fontSize: 14, lineHeight: 1.5 }}>{refus}</p>
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div style={cadre}>
        <div style={{ ...carte, textAlign: 'center', color: C.muted }} role="status">
          Chargement…
        </div>
      </div>
    );
  }
  if (code && !ouvert) {
    return (
      <div style={cadre}>
        <div style={carte}>
          <EtapeCode etat={code} onConnecte={entre} vous />
        </div>
      </div>
    );
  }
  if (ouvert) {
    return (
      <div style={cadre}>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 34 }}>✅</div>
          <h1 style={{ fontSize: 20, margin: '6px 0' }}>Bienvenue, {f.prenom} !</h1>
          <p style={{ color: C.grey, fontSize: 14, lineHeight: 1.5, margin: 0 }}>
            Votre compte est ouvert : vous commandez chez <b>{page.magasin}</b>, aux prix de{' '}
            <b>{page.client}</b>.
          </p>
        </div>
        <div style={{ maxWidth: 460, margin: '0 auto' }}>
          <GuideInstallation
            vous
            nom={NOM_LOGICIEL}
            apres="Vous l’ouvrez, vous commandez : votre adresse e-mail et votre mot de passe suffisent."
          />
        </div>
        <div style={{ maxWidth: 460, margin: '0 auto' }}>
          <a
            href="/"
            style={{
              display: 'block',
              textAlign: 'center',
              background: C.green,
              color: '#fff',
              borderRadius: 14,
              padding: '15px 12px',
              fontSize: 16,
              fontWeight: 900,
              textDecoration: 'none',
            }}
          >
            🛒 Commencer à commander
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={cadre}>
      <div style={carte}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.grey, letterSpacing: 0.5 }}>
          {page.magasin}
        </div>
        <h1 style={{ fontSize: 21, margin: '6px 0 6px' }}>Bonjour {page.prenom || ''} 👋</h1>
        <p style={{ fontSize: 14, color: C.grey, lineHeight: 1.5, margin: 0 }}>
          <b>{page.magasin}</b> vous invite à commander le matériel en ligne pour{' '}
          <b>{page.client}</b>, à vos prix. Créez votre compte : vous l’utiliserez pour commander
          {page.role === 'RESPONSABLE' ? ' et inviter votre équipe' : ''}.
        </p>
      </div>
      <form style={carte} onSubmit={valide}>
        <label style={etiquette}>
          Prénom
          <input
            style={champ}
            value={f.prenom}
            onChange={maj('prenom')}
            autoComplete="given-name"
          />
        </label>
        <label style={etiquette}>
          Nom
          <input style={champ} value={f.nom} onChange={maj('nom')} autoComplete="family-name" />
        </label>
        <label style={etiquette}>
          Adresse e-mail (pour vous connecter)
          <input
            style={champ}
            type="email"
            inputMode="email"
            value={f.email}
            onChange={maj('email')}
            autoComplete="email"
          />
        </label>
        <label style={etiquette}>
          Mot de passe ({LONGUEUR_MOT_DE_PASSE} caractères au moins)
          <input
            style={champ}
            type="password"
            value={f.motDePasse}
            onChange={maj('motDePasse')}
            autoComplete="new-password"
          />
        </label>
        <label style={etiquette}>
          Encore une fois
          <input
            style={champ}
            type="password"
            value={f.confirmation}
            onChange={maj('confirmation')}
            autoComplete="new-password"
          />
        </label>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.45, marginTop: 10 }}>
          Vous avez déjà un compte SwissTech avec cette adresse ? Entrez son mot de passe :{' '}
          {page.magasin} s’ajoute à vos sociétés.
        </div>
        {erreur && (
          <div role="alert" style={{ color: C.red, fontSize: 13, fontWeight: 700, marginTop: 10 }}>
            {erreur}
          </div>
        )}
        <button
          type="submit"
          disabled={envoi}
          style={{
            width: '100%',
            marginTop: 16,
            border: 'none',
            borderRadius: 14,
            padding: '15px 12px',
            background: C.green,
            color: '#fff',
            fontSize: 16,
            fontWeight: 900,
            cursor: 'pointer',
            fontFamily: 'inherit',
            opacity: envoi ? 0.7 : 1,
          }}
        >
          {envoi ? 'Création…' : '✓ Créer mon compte et entrer'}
        </button>
      </form>
    </div>
  );
}
